import React, { useState } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, loginWithGoogle, user, isAdmin, isAuthor, isPendingContributor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectAfterLogin = (loggedInUser, pending) => {
    if (pending || (loggedInUser?.role === 'author' && loggedInUser?.status === 'pending')) {
      navigate('/pending-approval');
      return;
    }
    if (loggedInUser?.role === 'admin' || (loggedInUser?.role === 'author' && loggedInUser?.status === 'active')) {
      navigate('/admin');
      return;
    }
    navigate(location.state?.from || '/');
  };

  if (user) {
    if (isPendingContributor) return <Navigate to="/pending-approval" replace />;
    if (isAdmin || isAuthor) return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(username, password);
    setSubmitting(false);
    if (result.success) {
      redirectAfterLogin(result.user, result.pendingApproval);
    } else {
      setError(result.error || 'Invalid username or password');
    }
  };

  const handleGoogle = async (credential) => {
    setError('');
    setSubmitting(true);
    const result = await loginWithGoogle({ credential });
    setSubmitting(false);
    if (result.success) {
      redirectAfterLogin(result.user, result.pendingApproval);
    } else {
      setError(result.error || 'Google sign-in failed');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card auth-card-wide">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <LogIn size={48} color="#0f766e" />
        </div>
        <h2 className="login-title">Sign in</h2>
        <p className="auth-subtitle">
          Members can leave feedback. Contributors and admins can manage content after signing in.
        </p>

        {error && <div className="auth-alert auth-alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username or email</label>
            <input
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
            <LogIn size={16} />
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="auth-divider"><span>or</span></div>

        <GoogleSignInButton onCredential={handleGoogle} disabled={submitting} />

        <p className="auth-footer-text">
          Don&apos;t have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
